<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { PRIORIDADE_NA_TELA } from 'dashboard/constants/prioridadeDaConversa';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import { dispatchWithAlert } from 'dashboard/helper/dispatchWithAlert';

export default {
  components: {
    AdvupSelect,
    ConversationLabels,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      // Este componente é de opções (não `script setup`), então o que o
      // template lê precisa estar aqui — a constante importada sozinha não
      // chega lá.
      PRIORIDADE_NA_TELA,
      // Em ordem de urgência, da nenhuma à urgente — é a ordem em que a
      // fileira de pílulas é lida.
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          tom: 'nenhuma',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          tom: 'cinza',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          tom: 'azul',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          tom: 'ambar',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          tom: 'rubi',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    // advup — 2026-07-28: seletor de responsável precisa listar também os
    // AgentBots (ex: Andie, atribuída via assignee_agent_bot quando a
    // cadência de follow-up assume a conversa) — sem isso o atendente não
    // consegue ver/tirar/reatribuir uma conversa que está com o bot.
    agentBotOptions() {
      const bots = this.$store.getters['agentBots/getBots'] || [];
      return bots.map(bot => ({
        ...bot,
        available_name: bot.name,
        isAgentBot: true,
      }));
    },
    assigneeOptions() {
      return [...this.agentsList, ...this.agentBotOptions];
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const conversationId = this.currentChat.id;
        const anterior = this.currentChat.meta.assignee;
        const agentId = agent ? agent.id : null;
        const assigneeType = agent && agent.isAgentBot ? 'AgentBot' : 'User';
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId,
          assignee: agent,
        });
        // 🔴 A tela troca antes do servidor responder; se ele recusar, volta
        // quem estava. Antes ficava mostrando o atendente que não foi gravado.
        dispatchWithAlert(
          this.$store.dispatch('assignAgent', {
            conversationId,
            agentId,
            assigneeType,
          }),
          {
            success: this.$t('CONVERSATION.CHANGE_AGENT'),
            failure: this.$t('CONVERSATION.CHANGE_AGENT_FAILED'),
          }
        ).then(({ ok }) => {
          if (ok) return;
          this.$store.dispatch('setCurrentChatAssignee', {
            conversationId,
            assignee: anterior,
          });
        });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const anterior = this.currentChat.meta.team;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        dispatchWithAlert(
          this.$store.dispatch('assignTeam', { conversationId, teamId }),
          {
            success: this.$t('CONVERSATION.CHANGE_TEAM'),
            failure: this.$t('CONVERSATION.CHANGE_TEAM_FAILED'),
          }
        ).then(({ ok }) => {
          if (ok) return;
          this.$store.dispatch('setCurrentChatTeam', {
            team: anterior,
            conversationId,
          });
        });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        dispatchWithAlert(
          this.$store.dispatch('assignPriority', { conversationId, priority }),
          {
            success: this.$t(
              'CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL',
              {
                priority: priorityItem.name,
                conversationId,
              }
            ),
            failure: this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.FAILED'),
          }
        ).then(({ ok }) => {
          if (!ok) {
            // Desfaz o update otimista quando a API recusa.
            this.$store.dispatch('setCurrentChatPriority', {
              priority: oldValue,
              conversationId,
            });
            return;
          }
          useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
            oldValue,
            newValue: priority,
            from: 'Conversation Sidebar',
          });
        });
      },
    },
    // O seletor precisa de um valor só; atendente e assistente (AgentBot)
    // têm sequências de id independentes, então o tipo vai junto no valor.
    chaveDoAtendente() {
      const atual = this.currentChat.meta?.assignee;
      if (!atual) return '';
      const tipo = this.currentChat.meta?.assignee_type || 'User';
      return `${tipo}:${atual.id}`;
    },
    opcoesDeAtendente() {
      const equipe = this.$t('CONVERSATION_SIDEBAR.GROUPS.TEAM');
      const assistentes = this.$t('CONVERSATION_SIDEBAR.GROUPS.ASSISTANTS');
      return [
        { label: this.$t('CONVERSATION_SIDEBAR.NOBODY'), value: '' },
        ...this.agentsList.map(agente => ({
          label: agente.available_name || agente.name,
          value: `User:${agente.id}`,
          color:
            agente.availability_status === 'online'
              ? 'rgb(var(--teal-9))'
              : 'rgb(var(--slate-8))',
          group: equipe,
        })),
        ...this.agentBotOptions.map(bot => ({
          label: bot.name,
          value: `AgentBot:${bot.id}`,
          color: 'rgb(var(--iris-9))',
          group: assistentes,
        })),
      ];
    },
    opcoesDeTime() {
      return [
        { label: this.$t('CONVERSATION_SIDEBAR.NO_TEAM'), value: 0 },
        ...this.teams.map(time => ({ label: time.name, value: time.id })),
      ];
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
  },
  methods: {
    escolherAtendente(chave) {
      if (chave === this.chaveDoAtendente) return;
      if (!chave) {
        this.assignedAgent = null;
        return;
      }
      const [tipo, id] = chave.split(':');
      const lista =
        tipo === 'AgentBot' ? this.agentBotOptions : this.agentsList;
      const escolhido = lista.find(item => String(item.id) === id);
      if (escolhido) this.assignedAgent = escolhido;
    },
    escolherTime(id) {
      if (id === (this.assignedTeam?.id || 0)) return;
      this.assignedTeam = id ? this.teams.find(time => time.id === id) : null;
    },
    escolherPrioridade(opcao) {
      if (opcao.id === (this.currentChat.priority || null)) return;
      this.assignedPriority = opcao;
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
  },
};
</script>

<template>
  <div class="cva">
    <div class="cva__campo">
      <div class="cva__topo">
        <span class="advup-rotulo">
          {{ $t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL') }}
        </span>
        <button
          v-if="showSelfAssign"
          type="button"
          class="cva__assumir"
          @click="onSelfAssign"
        >
          <i class="i-lucide-hand" aria-hidden="true" />
          {{ $t('CONVERSATION_SIDEBAR.SELF_ASSIGN') }}
        </button>
      </div>
      <AdvupSelect
        :model-value="chaveDoAtendente"
        :options="opcoesDeAtendente"
        :rotulo="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
        min-width="100%"
        @update:model-value="escolherAtendente"
      />
    </div>

    <div class="cva__campo">
      <span class="advup-rotulo">
        {{ $t('CONVERSATION_SIDEBAR.TEAM_LABEL') }}
      </span>
      <AdvupSelect
        :model-value="assignedTeam?.id || 0"
        :options="opcoesDeTime"
        :rotulo="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
        min-width="100%"
        @update:model-value="escolherTime"
      />
    </div>

    <!-- Desligada por ora (ver PRIORIDADE_NA_TELA): o campo continua inteiro,
         com as cores e o dado no banco, esperando o dia de voltar. -->
    <div v-if="PRIORIDADE_NA_TELA" class="cva__campo">
      <span id="cva-prioridade" class="advup-rotulo">
        {{ $t('CONVERSATION.PRIORITY.TITLE') }}
      </span>
      <!-- Um toque, e a cor diz o peso: era um seletor de lista para cinco
           valores fixos. -->
      <div
        class="cva__prioridades"
        role="radiogroup"
        aria-labelledby="cva-prioridade"
      >
        <button
          v-for="opcao in priorityOptions"
          :key="opcao.tom"
          type="button"
          role="radio"
          class="cva__prio"
          :class="[
            `cva__prio--${opcao.tom}`,
            { 'cva__prio--on': assignedPriority.id === opcao.id },
          ]"
          :aria-checked="assignedPriority.id === opcao.id"
          @click="escolherPrioridade(opcao)"
        >
          <span class="cva__prio-ponto" aria-hidden="true" />
          {{ opcao.name }}
        </button>
      </div>
    </div>

    <div class="cva__campo">
      <span class="advup-rotulo">
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS') }}
      </span>
      <ConversationLabels :conversation-id="conversationId" />
    </div>
  </div>
</template>

<style scoped>
/* Atribuição da conversa (aba CRM da ficha): seletores do produto e a
   prioridade em pílulas no tom de cada peso. */
.cva {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}

.cva__campo {
  display: flex;
  flex-direction: column;
}

.cva__topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
}

.cva__assumir {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  margin-bottom: var(--esp-1);
  padding: 0;
  border: 0;
  background: none;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--blue-11));
  cursor: pointer;
}

.cva__assumir > i {
  width: var(--ico-1);
  height: var(--ico-1);
  transition: rotate var(--mov-2) var(--mov-curva-pulo);
}

.cva__assumir:hover > i {
  rotate: -12deg;
}

.cva__prioridades {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-1);
}

.cva__prio {
  --prio: rgb(var(--slate-9));
  --prio-fundo: rgb(var(--slate-3));
  --prio-tinta: rgb(var(--slate-12));

  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  height: 28px;
  padding: 0 var(--esp-2);
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-redondo);
  background: rgb(var(--solid-1));
  font-size: var(--txt-nota);
  font-weight: var(--peso-dado);
  color: rgb(var(--slate-11));
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva-pulo);
}

.cva__prio:hover {
  border-color: var(--prio);
}

.cva__prio:active {
  scale: 0.95;
}

.cva__prio:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.cva__prio-ponto {
  width: 7px;
  height: 7px;
  border-radius: var(--r-redondo);
  background: var(--prio);
}

.cva__prio--nenhuma .cva__prio-ponto {
  background: none;
  box-shadow: inset 0 0 0 1.5px rgb(var(--slate-8));
}

.cva__prio--azul {
  --prio: rgb(var(--blue-9));
  --prio-fundo: rgb(var(--blue-3));
  --prio-tinta: rgb(var(--blue-11));
}

.cva__prio--ambar {
  --prio: rgb(var(--amber-9));
  --prio-fundo: rgb(var(--amber-3));
  --prio-tinta: color-mix(
    in srgb,
    rgb(var(--amber-12)) 18%,
    rgb(var(--amber-11))
  );
}

.cva__prio--rubi {
  --prio: rgb(var(--ruby-9));
  --prio-fundo: rgb(var(--ruby-3));
  --prio-tinta: rgb(var(--ruby-11));
}

.cva__prio--on {
  border-color: var(--prio);
  background: var(--prio-fundo);
  font-weight: var(--peso-forte);
  color: var(--prio-tinta);
  animation: advupPop var(--mov-2) var(--mov-curva-pulo);
}

@media (prefers-reduced-motion: reduce) {
  .cva__prio,
  .cva__assumir > i {
    transition: none;
  }

  .cva__prio--on {
    animation: none;
  }
}
</style>
